import React, { createRef } from 'react';
import classnames from 'classnames';
import clip, { MODE_TOUCH } from 'sunzi-plugin-clip';
import Path from 'sunzi-plugin-clip/lib/core/path';
import Carousel from 'nuka-carousel';
import { i18n, loadHTMLImage } from '@/utils';
import Icon from '../icon';
import Guide from '../guide';
import ImageLoad from '../image-load';
import styles from './style.less';

interface ClipProps {
  /** 渲染顶部 */
  renderHeader: React.ReactNode;
  /** 初始的base64 */
  source?: string;
  /** 主题 */
  theme?: 'dark' | 'light',
  gtag: any;
}

interface ClipState {
  revokeDisabled: boolean;
  gusture: MODE_TOUCH;
  guideVisible: boolean;
}

class Clip extends React.Component<ClipProps, ClipState > {

  private _element: React.RefObject<HTMLDivElement> = createRef();
  private _canvas:  React.RefObject<HTMLCanvasElement> = createRef();
  private _clip?: clip;

  constructor(props: ClipProps) {
    super(props);
    // 引导 第一次进入的时候 主动弹出
    const guideVisible = !!parseInt(window.localStorage.getItem("_sunzi_clip_guide") || "1");
    this.state = {
      revokeDisabled: true,
      gusture: MODE_TOUCH.pan,
      guideVisible,
    }
  }

  async componentDidMount() {
    const { source } = this.props;
    if (this._element.current && this._canvas.current && source) {
      // canvas 在safari可控下尺寸最大化
      const { offsetWidth, offsetHeight } = this._element.current;
      const scale = Math.min(1200 / offsetWidth, 1200 / offsetHeight);
      this._canvas.current.width = offsetWidth * scale;
      this._canvas.current.height = offsetHeight * scale;
      const _image = await loadHTMLImage(source);
      this._clip = new clip(this._canvas.current, _image, {
        lineWidth: 4,
        lineOutWidth: 10,
        lineColor: '#ff0136',
        lineOutColor: 'rgba(255, 255, 255, .2)',
        closedLineWidth: 10,
        closedLineOutWidth: 15,
        closedLineColor: 'rgba(255, 255, 255, 1)',
        closedLineOutColor: 'rgba(0, 0, 0, .5)',
        endpointColor: 'rgba(0, 0, 0, .5)',
        onStackChange: (stack: Path[]) => {
          const { revokeDisabled } = this.state;
          if (stack.length === 0 && !revokeDisabled) {
            this.setState({
              revokeDisabled: true
            });
          } else if (stack.length > 0 && revokeDisabled)
            this.setState({
              revokeDisabled: false
            });
        }
      });
    }
  }

  // 生成结果图
  public toClipURL = async () => {
    if (this._clip)
      return await this._clip.toClipURL();
    return;
  }

  /** 撤销 */
  handleRevoke = () => {
    if (this._clip) {
      this._clip.revoke();
      this.props.gtag('event', 'clip_footer_click', {
        'event_label': 'revoke'
      });
    }
  }

  /** 手势模式切换 */
  handleGustureChange = (type: MODE_TOUCH) => {
    this.setState({
      gusture: type,
    });
    this._clip?.modeToggle();
  }

  /** 放大 */
  handleZoomIn = () => {
    if (this._clip) {
      this._clip.zoomIn();
      this.props.gtag('event', 'clip_footer_click', {
        'event_label': 'zoom-in'
      });
    }
  }

  /** 缩小 */
  handleZoomOut = () => {
    if (this._clip) {
      this._clip.zoomOut();
      this.props.gtag('event', 'clip_footer_click', {
        'event_label': 'zoom-out'
      });
    }
  }

  /** 关闭引导 */
  onGuideVisible = (visible: boolean) => () => {
    window.localStorage.setItem("_sunzi_clip_guide", "0");
    this.setState({
      guideVisible: visible
    });
    if (visible) {
      this.props.gtag('event', 'clip_footer_click', {
        'event_label': 'help'
      });
    }
  }

  /** 销毁 */
  public dispose() {
    if (this._clip)
      this._clip.dispose();
  }

  render() {
    const { revokeDisabled, gusture, guideVisible } = this.state;
    const { renderHeader, theme } = this.props;

    return (
      <div className={classnames(styles.clip, {
        [styles.clipLight]: theme === 'light'
      })}>
        {renderHeader}
        <div ref={this._element} className={styles.clipContent}>
          <canvas ref={this._canvas} />
          <div className={styles.zoom}>
            <div
              className={styles.iconWrapper}
              onClick={this.handleZoomIn}
            >
              <Icon type="zoom-in" size={30} />
            </div>
            <div
              className={styles.iconWrapper}
              onClick={this.handleZoomOut}
            >
              <Icon type="zoom-out" size={30} />
            </div>
          </div>
        </div>
        <div className={styles.clipFooter}>
          <div
            className={classnames(styles.iconWrapper, {
              [styles.iconWrapperDisabled]: revokeDisabled
            })}
            onClick={this.handleRevoke}
          >
            <Icon type="revoke" size={30} />
            <span>{i18n.format("components.cropper.back")}</span>
          </div>
          <div
            className={classnames(styles.iconWrapper, {
              [styles.iconWrapperActive]: gusture === MODE_TOUCH.pan
            })}
            onClick={() => this.handleGustureChange(MODE_TOUCH.pan)}
          >
            <Icon type="scissors" size={30} />
            <span>{i18n.format("components.cropper.crop")}</span>
          </div>
          <div
            className={classnames(styles.iconWrapper, {
              [styles.iconWrapperActive]: gusture === MODE_TOUCH.move
            })}
            onClick={() => this.handleGustureChange(MODE_TOUCH.move)}
          >
            <Icon type="gesture-move" size={30} />
            <span>{i18n.format("components.cropper.move")}</span>
          </div>
        </div>
        {guideVisible &&
          <Guide onClose={this.onGuideVisible(false)}>
            <Carousel autoplay renderCenterLeftControls={null} renderCenterRightControls={null}>
              <ImageLoad source="https://sunzi7n.imaiyuan.com/sock/clip-guide-1.gif" style={{ width: 240, height: 240 }} />
              <ImageLoad source="https://sunzi7n.imaiyuan.com/sock/clip-guide-2.gif" style={{ width: 240, height: 240 }} />
              <ImageLoad source="https://sunzi7n.imaiyuan.com/sock/clip-guide-3.gif" style={{ width: 240, height: 240 }} />
            </Carousel>
          </Guide>
        }
      </div>
    );
  }
}

export default Clip;
